import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import type { HubSpot } from "../hubs";
import { fenceAt, groundKind, halfX, halfZ, railY, surfaceAt, type EdgeSide, type Level, type Prim, type Rail } from "../level";
import type { MapLook, PieceLook } from "../maps";
import { primKind } from "../prims";
import { bowlFloor } from "../prims/bowl";
import { CHUTE, type ChutePrim } from "../prims/chute";
import { POOL, poolCoping, type PoolPrim } from "../prims/pool";
import { buildBeach, buildBeachScenery, SAND, WALL_SIDES } from "./beach";
import { buildHubSigns } from "./hub-signs";
import { buildScenery } from "./scenery";
import { buildStreetEnd, localSpan, STREET_END_FLANK, wallSpans } from "./street-end";
import { mergeStatic } from "./merge";
import { buildShopRow, frontMaterials, shopFront, type AddMesh } from "./shop-row";
import { STREET_RUN, streetFronts } from "./shop-signs";
import { contactShadowTexture, concreteTexture, paintedMetalTexture, plywoodTexture } from "./textures";
import { activeStyle } from "./styles";
import { inked, toon, type ToyParams } from "./toon";

// Turns the Level heightfield description into meshes. Geometry here must match
// surfaceAt() in level.ts exactly or the skater will float or sink.

// Thin, faint ink edges on the park, if the art style draws them: the soft
// shading carries the shapes. One shared material per ink and opacity.
const edgeInks = new Map<string, THREE.LineBasicMaterial>();

function withEdges(mesh: THREE.Mesh, angle = 25): THREE.Mesh {
  const { edges, ink } = activeStyle().outlines;
  if (edges <= 0) return mesh;
  const key = `${new THREE.Color(ink).getHexString()}:${edges}`;
  let mat = edgeInks.get(key);
  if (!mat) edgeInks.set(key, (mat = new THREE.LineBasicMaterial({ color: ink, transparent: true, opacity: edges })));
  mesh.add(new THREE.LineSegments(new THREE.EdgesGeometry(mesh.geometry, angle), mat));
  return mesh;
}

/** The park's shared surface textures, made once per map build, and the map's piece look (#115). */
interface Surfaces {
  plywood: THREE.Texture | null;
  metal: THREE.Texture | null;
  /** Bare concrete pieces in these colors; null for the painted skatepark. */
  pieces: PieceLook | null;
}

/**
 * A piece's material when it has its own color or the map gives its pieces
 * a look (#115): concrete where the map does, paint in the piece's color
 * where only the piece does. Null for the skatepark's usual paint.
 */
function pieceMaterial(surf: Surfaces, own: string | undefined, role: "block" | "ramp", shade = 1): THREE.MeshStandardMaterial | null {
  const color = own ?? surf.pieces?.[role];
  if (color === undefined) return null;
  const c = new THREE.Color(color).multiplyScalar(shade);
  return surf.pieces ? toon(c, { roughness: 0.92 }, "concrete") : toon(c, { roughness: 0.6 }, "paint");
}

function withMap(tex: THREE.Texture | null, extra: ToyParams = {}): ToyParams {
  return tex ? { map: tex, ...extra } : extra;
}

function place(obj: THREE.Object3D, p: Prim): void {
  obj.position.set(p.x, 0, p.z);
  obj.rotation.y = -p.rot; // local +x maps to the primitive's +u axis
}

function extrude(shape: THREE.Shape, width: number): THREE.ExtrudeGeometry {
  const g = new THREE.ExtrudeGeometry(shape, { depth: width, bevelEnabled: false, curveSegments: 1 });
  g.translate(0, 0, -width / 2);
  return g;
}

/** A box at least this tall (m) is a wall block, not something to skate. */
const WALL_BLOCK_H = 8;
/** A box lower than this (m) is a surface on the floor (a street), with no contact shadow. */
const FLAT_BOX_H = 0.1;

type MeshBuilder<P> = (p: P, surf: Surfaces) => THREE.Object3D[];

/**
 * Each primitive's meshes, in its local frame (x along its u axis, placed and
 * turned by `buildPrim`). Must match its `eval` in prims/ exactly, or the
 * skater will float or sink. A primitive kind missing here doesn't compile.
 */
const PRIM_MESHES: { readonly [K in Prim["kind"]]: MeshBuilder<Extract<Prim, { kind: K }>> } = {
  box(p, surf) {
    if (p.deco === "lamp") return streetLamp(p, surf);
    if (p.deco === "table" || p.deco === "bench") return picnicPart(p);
    if (p.deco === "cab") return crashedCab(p);
    // Bevelled a few cm; the heightfield's hard edge is hidden under the round-off.
    const bevel = Math.min(0.06, p.h / 4, p.hu / 3, p.hw / 3);
    // Too tall to skate: an inside wall (the Warehouse's side rooms, #78), so it looks like one.
    const mat =
      p.h >= WALL_BLOCK_H && p.color === undefined
        ? toon("#5f5963", { roughness: 0.85 }, "concrete")
        : (pieceMaterial(surf, p.color, "block") ?? toon("#f28f3b", { roughness: 0.6 }, "paint"));
    const mesh = new THREE.Mesh(new RoundedBoxGeometry(p.hu * 2, p.h, p.hw * 2, 3, bevel), mat);
    mesh.position.y = p.h / 2;
    mesh.castShadow = mesh.receiveShadow = true;
    if (p.deco === "planter" || p.deco === "tree") return [mesh, ...planterTop(p)];
    return [mesh];
  },
  kicker(p, surf) {
    const shape = new THREE.Shape([new THREE.Vector2(0, 0), new THREE.Vector2(p.len, p.h), new THREE.Vector2(p.len, 0)]);
    // A steel launch ramp: painted sides, a scuffed painted-metal deck. On a street spot (#115), a concrete bank.
    const face = pieceMaterial(surf, p.color, "ramp");
    const mesh = withEdges(
      new THREE.Mesh(
        extrude(shape, p.hw * 2),
        face
          ? [pieceMaterial(surf, p.color, "ramp", 0.86)!, face]
          : [toon("#2b6cb0", { roughness: 0.5 }, "paint"), toon("#4aa3ff", withMap(surf.metal, { roughness: 0.42, metalness: 0.15 }), "paintedMetal")],
      ),
    );
    mesh.castShadow = mesh.receiveShadow = true;
    return [mesh];
  },
  quarter(p, surf) {
    const pts = [new THREE.Vector2(0, 0)];
    const segs = 24;
    for (let i = 1; i <= segs; i++) {
      const a = (i / segs) * (Math.PI / 2);
      pts.push(new THREE.Vector2(p.r * Math.sin(a), p.r * (1 - Math.cos(a))));
    }
    // A spine side has no deck: the arc already ends at the coping.
    if (p.deck > 0) pts.push(new THREE.Vector2(p.r + p.deck, p.r));
    pts.push(new THREE.Vector2(p.r + p.deck, 0));
    const face = pieceMaterial(surf, p.color, "ramp");
    const mesh = withEdges(
      new THREE.Mesh(
        extrude(new THREE.Shape(pts), p.hw * 2),
        face
          ? [pieceMaterial(surf, p.color, "ramp", 0.86)!, face]
          : [toon("#b07a48", withMap(surf.plywood, { roughness: 0.75 }), "plywood"), toon("#f0c17a", withMap(surf.plywood, { roughness: 0.62 }), "plywood")],
      ),
      40,
    );
    mesh.castShadow = mesh.receiveShadow = true;
    // A bank with no coping to grind (#240) shows none: what looks grindable is.
    if (p.bare) return [mesh];
    const copingGeo = new THREE.CylinderGeometry(0.05, 0.05, p.hw * 2, 12);
    copingGeo.rotateX(Math.PI / 2);
    const coping = inked(copingGeo, toon("#d4dae3", { roughness: 0.28, metalness: 0.7 }, "metal"), 0.01);
    coping.position.set(p.r, p.r, 0);
    return [mesh, coping];
  },
  stairs(p, surf) {
    // The whole set as one mesh (#115): its side profile, step by step down along u, extruded across.
    const half = (p.steps * p.run) / 2;
    const pts = [new THREE.Vector2(-half, 0)];
    for (let i = 0; i < p.steps; i++) {
      const h = p.rise * (p.steps - i);
      pts.push(new THREE.Vector2(-half + i * p.run, h), new THREE.Vector2(-half + (i + 1) * p.run, h));
    }
    pts.push(new THREE.Vector2(half, 0));
    const mat = pieceMaterial(surf, p.color, "block") ?? toon("#f28f3b", { roughness: 0.6 }, "paint");
    const mesh = withEdges(new THREE.Mesh(extrude(new THREE.Shape(pts), p.hw * 2), mat));
    mesh.castShadow = mesh.receiveShadow = true;
    return [mesh];
  },
  hubba(p, surf) {
    // A granite ledge down beside a stair set (#152): its side profile, sloping from h down to hb along u, extruded across.
    const shape = new THREE.Shape([new THREE.Vector2(-p.hu, 0), new THREE.Vector2(-p.hu, p.h), new THREE.Vector2(p.hu, p.hb), new THREE.Vector2(p.hu, 0)]);
    const mat = pieceMaterial(surf, p.color, "block") ?? toon("#f28f3b", { roughness: 0.6 }, "paint");
    const mesh = withEdges(new THREE.Mesh(extrude(shape, p.hw * 2), mat));
    mesh.castShadow = mesh.receiveShadow = true;
    return [mesh];
  },
  bowl(p) {
    const { fu, fw } = bowlFloor(p);
    const k = p.corner;
    const arc = 8; // segments per corner
    // The rounded floor's outline pushed out by d: its four corner arcs, in order round the rim.
    const contour = (d: number): THREE.Vector2[] => {
      const pts: THREE.Vector2[] = [];
      for (const [cu, cw, a0] of BOWL_CORNERS) {
        for (let i = 0; i <= arc; i++) {
          const a = a0 + (i / arc) * (Math.PI / 2);
          pts.push(new THREE.Vector2(cu * (fu - k) + (k + d) * Math.cos(a), cw * (fw - k) + (k + d) * Math.sin(a)));
        }
      }
      return pts;
    };
    // The transition: rings of the contour up a quarter circle, as the quarter pipe's arc.
    const segs = 24;
    const rings = Array.from({ length: segs + 1 }, (_, i) => {
      const a = (i / segs) * (Math.PI / 2);
      return { pts: contour(p.r * Math.sin(a)), y: p.r * (1 - Math.cos(a)) };
    });
    const n = rings[0]!.pts.length;
    const pos: number[] = [];
    const idx: number[] = [];
    for (const ring of rings) for (const q of ring.pts) pos.push(q.x, ring.y, q.y);
    for (let i = 0; i < segs; i++) {
      for (let j = 0; j < n; j++) {
        const a = i * n + j;
        const b = i * n + ((j + 1) % n);
        // Wound so the faces look in, toward the middle of the pool.
        idx.push(a, b, a + n, b, b + n, a + n);
      }
    }
    const wallGeo = new THREE.BufferGeometry();
    wallGeo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    wallGeo.setIndex(idx);
    wallGeo.computeVertexNormals();
    const pool = toon("#8ec5e0", { roughness: 0.7 }, "concrete");
    const wall = withEdges(new THREE.Mesh(wallGeo, pool), 40);
    // The floor sits on the ground plane, so it's pulled forward to win over it and the contact shadow.
    const floorMat = toon("#8ec5e0", { roughness: 0.7, polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4 }, "concrete");
    const floorGeo = new THREE.ShapeGeometry(new THREE.Shape(contour(0).map((q) => new THREE.Vector2(q.x, -q.y))));
    floorGeo.rotateX(-Math.PI / 2);
    const floor = new THREE.Mesh(floorGeo, floorMat);
    // The raised block: its square outside with the rim cut out, deck on top.
    const block = new THREE.Shape([
      new THREE.Vector2(-p.hu, -p.hw),
      new THREE.Vector2(p.hu, -p.hw),
      new THREE.Vector2(p.hu, p.hw),
      new THREE.Vector2(-p.hu, p.hw),
    ]);
    block.holes.push(new THREE.Path(contour(p.r).map((q) => new THREE.Vector2(q.x, -q.y))));
    const blockGeo = new THREE.ExtrudeGeometry(block, { depth: p.r, bevelEnabled: false, curveSegments: 1 });
    blockGeo.rotateX(-Math.PI / 2);
    const deck = withEdges(new THREE.Mesh(blockGeo, [toon("#b8b0a2", { roughness: 0.85 }, "concrete"), toon("#8d8577", { roughness: 0.85 }, "concrete")]));
    for (const m of [wall, floor, deck]) m.castShadow = m.receiveShadow = true;
    // Coping all the way round the rim: a pipe along each straight, a quarter ring round each corner.
    const steel = toon("#d4dae3", { roughness: 0.28, metalness: 0.7 }, "metal");
    const copings: THREE.Object3D[] = [];
    const straight = (len: number, x: number, z: number, alongU: boolean) => {
      if (len < 0.05) return;
      const g = new THREE.CylinderGeometry(0.05, 0.05, len, 12);
      g.rotateX(Math.PI / 2);
      if (alongU) g.rotateY(Math.PI / 2);
      const c = inked(g, steel, 0.01);
      c.position.set(x, p.r, z);
      copings.push(c);
    };
    straight(2 * (fw - k), fu + p.r, 0, false);
    straight(2 * (fw - k), -(fu + p.r), 0, false);
    straight(2 * (fu - k), 0, fw + p.r, true);
    straight(2 * (fu - k), 0, -(fw + p.r), true);
    for (const [cu, cw, a0] of BOWL_CORNERS) {
      const g = new THREE.TorusGeometry(k + p.r, 0.05, 8, arc * 2, Math.PI / 2);
      g.rotateX(Math.PI / 2); // the ring flat, its arc from +x round to +z
      g.rotateY(-a0);
      const c = inked(g, steel, 0.01);
      c.position.set(cu * (fu - k), p.r, cw * (fw - k));
      copings.push(c);
    }
    return [wall, floor, deck, ...copings];
  },
  pool(p) {
    return poolMeshes(p);
  },
  chute(p, surf) {
    return chuteMeshes(p, surf);
  },
};

/** Sorted, with values closer than 1e-4 merged. */
function uniqueSorted(v: number[]): number[] {
  const out: number[] = [];
  for (const x of v.sort((a, b) => a - b)) if (!out.length || x - out[out.length - 1]! > 1e-4) out.push(x);
  return out;
}

/** Evenly from a to b, about `step` apart, both ends included. */
function spaced(a: number, b: number, step: number): number[] {
  const n = Math.max(1, Math.ceil((b - a) / step));
  return Array.from({ length: n + 1 }, (_, i) => a + ((b - a) * i) / n);
}

/** A transition from `start` (the floor's edge) out to the coping, `r` away, `sign` the way out: dense where it steepens. */
function wallSamples(start: number, r: number, sign: number): number[] {
  return Array.from({ length: 25 }, (_, i) => start + sign * r * Math.sin((i / 24) * (Math.PI / 2)));
}

/**
 * A water slide's chute (#163), one leg or bend: the trough's surface
 * sampled from the primitive's own `eval` (so it matches the sim), on
 * lines along it and across it that are dense up each wall, then its
 * outsides and ends dropped to the ground, since it's solid underneath.
 */
function chuteMeshes(p: ChutePrim, surf: Surfaces): THREE.Object3D[] {
  const b = p.bend;
  const len = b ? Math.abs(b.sweep) * (b.r + p.tw) : 2 * p.hu;
  const ss = spaced(0, 1, b ? 0.6 / len : 1 / Math.max(1, len));
  const ds = uniqueSorted([...spaced(-p.tw, p.tw, 0.5), ...wallSamples(p.tw - p.wall, p.wall, 1), ...wallSamples(-(p.tw - p.wall), p.wall, -1)]).filter((d) => Math.abs(d) <= p.tw);
  // Local (u, w) at fraction s along it and d across (+ to the left, +w on a straight).
  const at = (s: number, d: number): [number, number] => {
    if (!b) return [-p.hu + 2 * p.hu * s, d];
    const a = b.sweep * s;
    const r = b.r + d;
    return [r * Math.cos(a), r * Math.sin(a)];
  };
  // Heights a hair inside, so the edges and ends read the trough, not the ground past it.
  const hAt = (s: number, d: number): number => {
    const [u, w] = at(Math.min(1 - 1e-6, Math.max(1e-6, s)), Math.max(-p.tw + 1e-6, Math.min(p.tw - 1e-6, d)));
    return CHUTE.eval(p, u, w)?.h ?? 0;
  };
  const top: number[] = [];
  for (const s of ss) for (const d of ds) {
    const [u, w] = at(s, d);
    top.push(u, hAt(s, d), w);
  }
  const idx: number[] = [];
  const nd = ds.length;
  for (let i = 0; i + 1 < ss.length; i++) for (let j = 0; j + 1 < nd; j++) {
    const a = i * nd + j;
    idx.push(a, a + 1, a + nd, a + nd, a + 1, a + nd + 1);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(top, 3));
  geo.setIndex(idx);
  geo.computeVertexNormals();
  const slide = toon("#4cc9f0", { roughness: 0.35 }, "paint");
  slide.side = THREE.DoubleSide;
  const trough = withEdges(new THREE.Mesh(geo, slide), 40);
  trough.receiveShadow = true;
  // Its outsides (along each wall's top) and both ends, down to the ground.
  const skin: number[] = [];
  const quad = (p0: [number, number, number], p1: [number, number, number]) => {
    skin.push(p0[0], p0[1], p0[2], p1[0], p1[1], p1[2], p1[0], 0, p1[2]);
    skin.push(p0[0], p0[1], p0[2], p1[0], 0, p1[2], p0[0], 0, p0[2]);
  };
  const pt = (s: number, d: number): [number, number, number] => {
    const [u, w] = at(s, d);
    return [u, hAt(s, d), w];
  };
  for (const d of [-p.tw, p.tw]) for (let i = 0; i + 1 < ss.length; i++) quad(pt(ss[i]!, d), pt(ss[i + 1]!, d));
  for (const s of [0, 1]) for (let j = 0; j + 1 < nd; j++) quad(pt(s, ds[j]!), pt(s, ds[j + 1]!));
  const sg = new THREE.BufferGeometry();
  sg.setAttribute("position", new THREE.Float32BufferAttribute(skin, 3));
  sg.computeVertexNormals();
  const shellMat = pieceMaterial(surf, p.color, "block") ?? toon("#ffd166", { roughness: 0.6 }, "paint");
  shellMat.side = THREE.DoubleSide;
  const shell = new THREE.Mesh(sg, shellMat);
  shell.castShadow = shell.receiveShadow = true;
  return [trough, shell];
}

/**
 * An in-ground pool (#163): its surface as a heightfield sampled from the
 * primitive's own `eval`, so it matches the sim exactly, on a grid that's
 * dense up every transition and has a pair of lines either side of each
 * step's edge for the risers; then coping round the rim.
 */
function poolMeshes(p: PoolPrim): THREE.Object3D[] {
  const st = p.steps;
  const eps = 2e-3;
  const [w0, w1] = POOL.across!(p);
  const us = [
    ...spaced(-p.hu, p.hu, 0.5),
    ...wallSamples(-(p.hu - p.shallow), p.shallow, -1),
    ...wallSamples(p.hu - p.deep, p.deep, 1),
    ...spaced(p.hipFrom, p.hipTo, 0.25),
  ];
  const ws = [...spaced(-p.hw, p.hw, 0.5)];
  for (const r of [p.shallow, (p.shallow + p.deep) / 2, p.deep]) ws.push(...wallSamples(p.hw - r, r, 1), ...wallSamples(-(p.hw - r), r, -1));
  const a = p.arm;
  if (a) {
    // An L (#169): the arm's own walls, and lines either side of where the wings meet so the notch's edges are exact.
    const m = a.side === "w+" ? 1 : -1;
    const u0 = p.hu - 2 * a.hw;
    const far = p.hw + a.len;
    us.push(...wallSamples(u0 + p.deep, p.deep, -1));
    ws.push(...spaced(w0, w1, 0.5), ...wallSamples(m * (far - p.deep), p.deep, m), ...wallSamples(m * (p.hw + p.deep), p.deep, -m));
  }
  if (st) {
    for (let k = 1; k <= st.count; k++) us.push(-p.hu + k * st.run - eps, -p.hu + k * st.run + eps);
    for (const w of [st.w - st.hw, st.w + st.hw]) ws.push(w - eps, w + eps);
  }
  const U = uniqueSorted(us.filter((u) => Math.abs(u) <= p.hu));
  const W = uniqueSorted(ws.filter((w) => w >= w0 && w <= w1));
  const pos: number[] = [];
  for (const u of U) for (const w of W) pos.push(u, POOL.eval(p, u, w)?.h ?? 0, w);
  const idx: number[] = [];
  const nw = W.length;
  for (let i = 0; i + 1 < U.length; i++) {
    for (let j = 0; j + 1 < nw; j++) {
      // An L's notch is no part of it.
      if (a && !POOL.eval(p, (U[i]! + U[i + 1]!) / 2, (W[j]! + W[j + 1]!) / 2)) continue;
      const k = i * nw + j;
      const c = k + nw;
      // Wound to face up: +w then +u.
      idx.push(k, k + 1, c, c, k + 1, c + 1);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setIndex(idx);
  geo.computeVertexNormals();
  const shell = withEdges(new THREE.Mesh(geo, toon("#8ec5e0", { roughness: 0.7 }, "concrete")), 40);
  shell.receiveShadow = true;
  // Coping round the rim: a pipe along each straight, a quarter ring round each corner (its end's radius).
  const steel = toon("#d4dae3", { roughness: 0.28, metalness: 0.7 }, "metal");
  const out: THREE.Object3D[] = [shell];
  const { straights, corners } = poolCoping(p);
  for (const [u0, w0, u1, w1] of straights) {
    const len = Math.hypot(u1 - u0, w1 - w0);
    if (len < 0.05) continue;
    const g = new THREE.CylinderGeometry(0.05, 0.05, len, 12);
    g.rotateX(Math.PI / 2);
    g.rotateY(Math.atan2(u1 - u0, w1 - w0));
    const c = inked(g, steel, 0.01);
    c.position.set((u0 + u1) / 2, 0, (w0 + w1) / 2);
    out.push(c);
  }
  for (const k of corners) {
    const g = new THREE.TorusGeometry(k.r, 0.05, 8, 16, Math.PI / 2);
    g.rotateX(Math.PI / 2);
    // The arc runs a quarter turn from where it starts, centred on the way the corner turns out.
    g.rotateY(-(Math.atan2(k.sw, k.su) - Math.PI / 4));
    const c = inked(g, steel, 0.01);
    c.position.set(k.u, 0, k.w);
    out.push(c);
  }
  return out;
}

/**
 * A flat ground plane from (x0, z0) to (x1, z1) at height 0, with a hole
 * cut for every in-ground pool (#163); UVs run 0..1 across it like a
 * `PlaneGeometry`'s, so its texture repeats the same. Maps without pools
 * keep their plain `PlaneGeometry`.
 */
function groundPlane(level: Level, x0: number, x1: number, z0: number, z1: number): THREE.BufferGeometry {
  const pools = level.prims.filter((p): p is PoolPrim => p.kind === "pool");
  // Shape coordinates are (x, -z), so turning it flat (-90 degrees about x) puts them back at (x, z).
  const shape = new THREE.Shape([new THREE.Vector2(x0, -z1), new THREE.Vector2(x1, -z1), new THREE.Vector2(x1, -z0), new THREE.Vector2(x0, -z0)]);
  for (const p of pools) {
    const c = Math.cos(p.rot);
    const s = Math.sin(p.rot);
    const corner = (u: number, w: number) => new THREE.Vector2(p.x + u * c - w * s, -(p.z + u * s + w * c));
    const a = p.arm;
    if (!a) {
      shape.holes.push(new THREE.Path([corner(-p.hu, -p.hw), corner(-p.hu, p.hw), corner(p.hu, p.hw), corner(p.hu, -p.hw)]));
      continue;
    }
    // An L (#169): one hole round both wings.
    const m = a.side === "w+" ? 1 : -1;
    const u0 = p.hu - 2 * a.hw;
    const far = p.hw + a.len;
    const pts = [corner(-p.hu, -m * p.hw), corner(-p.hu, m * p.hw), corner(u0, m * p.hw), corner(u0, m * far), corner(p.hu, m * far), corner(p.hu, -m * p.hw)];
    // Holes wind the same way whichever side the arm is on.
    shape.holes.push(new THREE.Path(m > 0 ? pts : pts.reverse()));
  }
  const g = new THREE.ShapeGeometry(shape);
  g.rotateX(-Math.PI / 2);
  const pos = g.getAttribute("position");
  const uv = g.getAttribute("uv");
  for (let i = 0; i < pos.count; i++) uv.setXY(i, (pos.getX(i) - x0) / (x1 - x0), (z1 - pos.getZ(i)) / (z1 - z0));
  return g;
}

/** The bowl's corners round the rim: which way from the middle (u, w) and where its arc starts. */
const BOWL_CORNERS: readonly [number, number, number][] = [
  [1, 1, 0],
  [-1, 1, Math.PI / 2],
  [-1, -1, Math.PI],
  [1, -1, (3 * Math.PI) / 2],
];

/** Pieces drawn away from the park (#195: the hill road's ramps), in a look's materials. */
export function buildPieces(prims: readonly Prim[], look: MapLook): THREE.Object3D[] {
  if (!prims.length) return [];
  const surf: Surfaces = { plywood: plywoodTexture(), metal: paintedMetalTexture(), pieces: look.pieces ?? null };
  return prims.map((p) => buildPrim(p, surf));
}

function buildPrim<P extends Prim>(p: P, surf: Surfaces): THREE.Object3D {
  const group = new THREE.Group();
  place(group, p);
  const build = PRIM_MESHES[p.kind] as unknown as MeshBuilder<P>;
  group.add(...build(p, surf));
  return group;
}

function buildRail(rail: Rail, surf: Surfaces, base: (x: number, z: number) => number): THREE.Object3D | null {
  if (rail.kind === "coping") return null; // drawn with its quarter pipe
  if (rail.track) return null; // a coaster's track (#189), drawn by its scenery
  const group = new THREE.Group();
  const a = new THREE.Vector3(rail.a.x, rail.y, rail.a.z);
  const b = new THREE.Vector3(rail.b.x, railY(rail, 1), rail.b.z);
  const len = a.distanceTo(b);
  const mid = a.clone().add(b).multiplyScalar(0.5);
  const yaw = Math.atan2(b.x - a.x, b.z - a.z);
  // A handrail (#10) tips down (or up) toward b.
  const pitch = Math.atan2(b.y - a.y, Math.hypot(b.x - a.x, b.z - a.z));
  if (rail.kind === "ledge") {
    const edge = inked(
      new RoundedBoxGeometry(0.08, 0.06, len, 2, 0.012),
      toon("#a3adbd", { roughness: 0.3, metalness: 0.65 }, "metal"),
      0.008,
    );
    edge.position.copy(mid).setY(mid.y - 0.02);
    // A hubba's edges (#152) slope down with it.
    if (pitch === 0) edge.rotation.y = yaw;
    else edge.rotation.set(-pitch, yaw, 0, "YXZ");
    group.add(edge);
    return group;
  }
  const barGeo = new THREE.CylinderGeometry(0.045, 0.045, len, 12);
  barGeo.rotateX(Math.PI / 2);
  // Red painted steel in a skatepark; galvanized steel on a street spot (#115).
  const steel = surf.pieces ? toon(surf.pieces.rail, { roughness: 0.4, metalness: 0.6 }, "metal") : null;
  const bar = inked(barGeo, steel ?? toon("#e63946", withMap(surf.metal, { roughness: 0.35, metalness: 0.2 }), "paintedMetal"), 0.012);
  bar.position.copy(mid);
  if (pitch === 0) bar.rotation.y = yaw;
  else bar.rotation.set(-pitch, yaw, 0, "YXZ");
  group.add(bar);
  const posts = Math.max(2, Math.ceil(len / 3) + 1);
  // Rounded end caps, posts with a base plate bolted to the floor.
  const capGeo = new THREE.SphereGeometry(0.045, 12, 8);
  for (const end of [a, b]) {
    const cap = new THREE.Mesh(capGeo, bar.material);
    cap.position.copy(end);
    group.add(cap);
  }
  const levelPost = pitch === 0 ? new THREE.CylinderGeometry(0.04, 0.04, rail.y, 10) : null;
  const plateGeo = new THREE.CylinderGeometry(0.1, 0.11, 0.025, 16);
  const postMat = steel ?? toon("#5b6272", withMap(surf.metal, { roughness: 0.4, metalness: 0.3 }), "paintedMetal");
  for (let i = 0; i < posts; i++) {
    const p = a.clone().lerp(b, i / (posts - 1));
    // Posts stand on the floor, even under a handrail going down a stair set.
    const y0 = base(p.x, p.z);
    const post = inked(levelPost && y0 === 0 ? levelPost : new THREE.CylinderGeometry(0.04, 0.04, p.y - y0, 10), postMat, 0.01);
    post.position.set(p.x, (p.y + y0) / 2, p.z);
    group.add(post);
    const plate = new THREE.Mesh(plateGeo, postMat);
    plate.position.set(p.x, y0 + 0.012, p.z);
    plate.receiveShadow = true;
    group.add(plate);
  }
  return group;
}

function buildTree(x: number, z: number, s: number): THREE.Object3D {
  const tree = new THREE.Group();
  const trunk = inked(new THREE.CylinderGeometry(0.25 * s, 0.35 * s, 2 * s, 10), toon("#8b5a2b", { roughness: 0.9 }, "plywood"), 0.03);
  trunk.position.y = s;
  tree.add(trunk);
  for (const [y, r] of [
    [2.6, 1.8],
    [3.8, 1.4],
    [4.8, 0.9],
  ] as const) {
    const leaves = inked(new THREE.IcosahedronGeometry(r * s, 2), toon("#3fae5a", { roughness: 0.8 }, "grass"), 0.05);
    leaves.position.y = y * s;
    tree.add(leaves);
  }
  tree.position.set(x, 0, z);
  return tree;
}

/** Soil and shrubs on a planter box (#115), or a street tree growing out of it, in its local frame. */
function planterTop(p: Extract<Prim, { kind: "box" }>): THREE.Object3D[] {
  const out: THREE.Object3D[] = [];
  const rim = Math.min(0.15, p.hu / 3, p.hw / 3);
  const soil = new THREE.Mesh(new THREE.BoxGeometry((p.hu - rim) * 2, 0.04, (p.hw - rim) * 2), toon("#4a3a2c", { roughness: 1 }, "grass"));
  soil.position.y = p.h + 0.005;
  soil.receiveShadow = true;
  out.push(soil);
  const leaf = toon("#4f8f46", { roughness: 0.85 }, "grass");
  // Shrubs in a row down the long side, one per metre or so.
  const along = p.hu >= p.hw;
  const len = (along ? p.hu : p.hw) - rim;
  const n = Math.max(1, Math.round(len));
  for (let i = 0; i < n; i++) {
    const t = -len + ((i + 0.5) * 2 * len) / n;
    const r = Math.min(0.42, (along ? p.hw : p.hu) - rim);
    const shrub = inked(new THREE.IcosahedronGeometry(r, 1), leaf, 0.02);
    shrub.scale.y = 0.75;
    shrub.position.set(along ? t : 0, p.h + r * 0.55, along ? 0 : t);
    out.push(shrub);
  }
  if (p.deco === "tree") {
    const tree = buildTree(0, 0, 0.8);
    tree.position.y = p.h;
    out.push(tree);
  }
  return out;
}

/**
 * A picnic table's top or bench (#167), in its local frame: a wooden slab the
 * box's size at its top, on a plank leg near each end. The sim sees the whole
 * box, so you ride over it and bump into it, never under it.
 */
function picnicPart(p: Extract<Prim, { kind: "box" }>): THREE.Object3D[] {
  const wood = toon(p.color ?? "#9a6b43", { roughness: 0.85 }, "plywood");
  const slabH = Math.min(0.07, p.h / 3);
  const slab = new THREE.Mesh(new RoundedBoxGeometry(p.hu * 2, slabH, p.hw * 2, 2, Math.min(0.02, slabH / 3)), wood);
  slab.position.y = p.h - slabH / 2;
  const part = new THREE.Group();
  part.add(slab);
  for (const s of [-1, 1]) {
    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.08, p.h - slabH, p.hw * 1.6), wood);
    leg.position.set(s * (p.hu - 0.3), (p.h - slabH) / 2, 0);
    part.add(leg);
  }
  for (const m of part.children) m.castShadow = m.receiveShadow = true;
  // One mesh per part, so the street's tables cost a draw call each (#186).
  return [mergeStatic(part)];
}

/**
 * A crashed taxi under plywood (#246), the box's size: a yellow body sunk to
 * its sills, the cabin's dark glass and its roof at the box's top, and two
 * sheets thrown over the hood and the trunk at that height, so what you
 * ride over is what you see. Its own made-up cab company's checker stripe.
 */
function crashedCab(p: Extract<Prim, { kind: "box" }>): THREE.Object3D[] {
  const yellow = toon(p.color ?? "#f2c230", { roughness: 0.45 }, "paint");
  const glass = toon("#1d2328", { roughness: 0.2, metalness: 0.3 }, "glass");
  const black = toon("#1b1c1e", { roughness: 0.8 }, "plastic");
  const wood = toon("#c39a62", { roughness: 0.85 }, "plywood");
  const len = p.hu * 2;
  const wide = p.hw * 2;
  const bodyH = p.h * 0.62;
  const cabin = len * 0.48;
  const part = new THREE.Group();
  const add = (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number, yaw = 0) => {
    const m = new THREE.Mesh(geo, mat);
    m.position.set(x, y, z);
    m.rotation.y = yaw;
    part.add(m);
  };
  add(new RoundedBoxGeometry(len, bodyH, wide, 2, 0.08), yellow, 0, bodyH / 2, 0);
  // The cabin: glass all round, the roof panel on top at the box's height.
  add(new THREE.BoxGeometry(cabin, p.h - bodyH - 0.06, wide * 0.9), glass, 0, (p.h + bodyH - 0.06) / 2, 0);
  add(new RoundedBoxGeometry(cabin + 0.1, 0.06, wide * 0.94, 2, 0.02), yellow, 0, p.h - 0.03, 0);
  // A checker stripe along each side, under the windows.
  for (const s of [-1, 1]) {
    for (let i = 0; i < 12; i++) {
      add(new THREE.BoxGeometry(len / 12, 0.07, 0.01), i % 2 ? black : yellow, -len / 2 + (i + 0.5) * (len / 12), bodyH * 0.72, s * (wide / 2 + 0.005));
    }
    for (const x of [-1, 1]) add(new THREE.CylinderGeometry(0.3, 0.3, 0.2, 14).rotateX(Math.PI / 2), black, x * len * 0.32, 0.18, s * (wide / 2 - 0.08));
  }
  // Plywood over the hood and the trunk, a little askew, at the roof's height.
  for (const x of [-1, 1]) add(new THREE.BoxGeometry((len - cabin) / 2 + 0.25, 0.03, wide * 1.05), wood, (x * (len + cabin)) / 4, p.h - 0.015, 0, x * 0.06);
  for (const m of part.children) m.castShadow = m.receiveShadow = true;
  return [mergeStatic(part)];
}

/** A street lamp (#115): a concrete plinth the box's size, a steel post on it, an arm and a lamp head, as tall as the box. */
function streetLamp(p: Extract<Prim, { kind: "box" }>, surf: Surfaces): THREE.Object3D[] {
  const steel = toon(surf.pieces?.rail ?? "#5b6272", { roughness: 0.45, metalness: 0.5 }, "metal");
  const plinthH = 0.7;
  const base = new THREE.Mesh(
    new RoundedBoxGeometry(p.hu * 2, plinthH, p.hw * 2, 2, 0.05),
    pieceMaterial(surf, p.color, "block") ?? toon("#9d978e", { roughness: 0.9 }, "concrete"),
  );
  base.position.y = plinthH / 2;
  base.castShadow = base.receiveShadow = true;
  const post = inked(new THREE.CylinderGeometry(0.07, 0.11, p.h - plinthH, 10), steel, 0.01);
  post.position.y = plinthH + (p.h - plinthH) / 2;
  const arm = inked(new THREE.BoxGeometry(1.1, 0.08, 0.08), steel, 0.01);
  arm.position.set(0.5, p.h - 0.1, 0);
  const head = inked(new THREE.BoxGeometry(0.55, 0.14, 0.3), steel, 0.01);
  head.position.set(1, p.h - 0.2, 0);
  const light = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.02, 0.22), new THREE.MeshBasicMaterial({ color: "#fff3cf" }));
  light.position.set(1, p.h - 0.28, 0);
  for (const m of [base, post, arm, head]) m.castShadow = true;
  return [base, post, arm, head, light];
}

/** Facade colors along a street (#115): brick, stone, render and a glass tower's grey. */
const FRONT_COLORS = ["#9b5d45", "#c8b89c", "#8d8f93", "#b5836a", "#d6ccbb", "#6e7680", "#a8765c"];

/**
 * What's round a `street` park (#115): an east-west street at street level.
 * A row of buildings stands right behind the north and south walls (their
 * fronts are those walls: windows floor by floor, a shopfront and a
 * cornice, some with an awning), and carries on past the east and west
 * barriers, where the street goes on between sidewalks with lamp posts and
 * street trees. Fixed layout, so every build looks the same; one mesh per
 * material (`mergeStatic`).
 */
function buildStreet(hx: number, hz: number, walls: readonly string[]): THREE.Object3D {
  const around = new THREE.Group();
  const m = frontMaterials();
  const pavement = toon("#aaa49b", { roughness: 0.9 }, "concrete");
  // Every shopfront is a made-up local business (#152): its sign and its awning.
  const fronts = FRONT_COLORS.map((c) => toon(c, { roughness: 0.85 }, "concrete"));
  const add: AddMesh = (geo, mat, x, y, z, cast = true) => {
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(x, y, z);
    mesh.castShadow = cast;
    mesh.receiveShadow = true;
    around.add(mesh);
    return mesh;
  };
  // Sidewalks past the barriers, along each row of buildings.
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      add(new THREE.BoxGeometry(STREET_RUN, 0.15, 4), pavement, sx * (hx + STREET_RUN / 2 + 0.5), 0.075, sz * (hz - 2), false);
    }
  }
  // The side's wall color is the building behind the middle of it, so a map still picks something.
  const firsts = { [-1]: toon(walls[2]!, { roughness: 0.85 }, "concrete"), 1: toon(walls[0]!, { roughness: 0.85 }, "concrete") };
  for (const { side: sz, x, w, h, shop: k } of streetFronts(hx)) {
    const wallMat = x <= 0 && x + w > 0 ? firsts[sz] : fronts[k % fronts.length]!;
    shopFront(add, around, m, { x, w, h, d: 14, shop: k }, sz * (hz + 0.05), sz, wallMat); // the front, on the fence line
  }
  // Lamp posts and street trees along the sidewalks past the barriers.
  const post = toon("#4a5058", { roughness: 0.45, metalness: 0.5 }, "metal");
  const bulb = new THREE.MeshBasicMaterial({ color: "#fff3cf" });
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      for (let t = 6; t < STREET_RUN; t += 12) {
        const x = sx * (hx + t);
        const z = sz * (hz - 3.6);
        add(new THREE.CylinderGeometry(0.07, 0.1, 5, 8), post, x, 2.5, z);
        add(new THREE.BoxGeometry(0.5, 0.14, 0.3), post, x, 5, z + sz * 0.3);
        add(new THREE.BoxGeometry(0.4, 0.02, 0.2), bulb, x, 4.92, z + sz * 0.3, false);
        const tree = buildTree(x + sx * 6, z, 0.9);
        tree.traverse((o) => {
          if (o instanceof THREE.Mesh && o.name !== "outline") o.castShadow = false;
        });
        around.add(tree);
      }
    }
  }
  return mergeStatic(around);
}

/** The park's meshes: floor, walls, pieces, rails, what's outside, and the challenge hubs' signs (#87). */
export function buildLevelMeshes(level: Level, look: MapLook, hubs: readonly HubSpot[] = []): THREE.Group {
  if (!level.hill) return buildPark(level, look, hubs);
  // On a hill (#193) the whole park is built flat, then sheared down toward
  // +z the way `surfaceAt` tilts it: heights fall by the grade per metre, and
  // anything upright stays upright. The sky added later stays level.
  // What's in the road (#194) moves with the road (#195), so it's drawn on
  // its own (`render/hill-course.ts`), the road's ramps and grind rails (#237) with it.
  const park = buildPark({ ...level, prims: level.hill.parkPrims, rails: level.hill.parkRails }, look, hubs);
  park.matrixAutoUpdate = false;
  park.matrix.set(1, 0, 0, 0, 0, 1, -level.hill.grade, 0, 0, 0, 1, 0, 0, 0, 0, 1);
  const root = new THREE.Group();
  root.add(park);
  return root;
}

function buildPark(level: Level, look: MapLook, hubs: readonly HubSpot[]): THREE.Group {
  const root = new THREE.Group();
  const size = level.bounds * 2;
  // A rectangular park (#115) is this long east to west and north to south; both are `size` on a square one.
  const [sizeX, sizeZ] = [halfX(level) * 2, halfZ(level) * 2];

  const tex = concreteTexture();
  if (tex) tex.repeat.set(sizeX / 4, sizeZ / 4);
  const hasPools = level.prims.some((p) => p.kind === "pool");
  const floor = new THREE.Mesh(
    hasPools ? groundPlane(level, -sizeX / 2, sizeX / 2, -sizeZ / 2, sizeZ / 2) : new THREE.PlaneGeometry(sizeX, sizeZ),
    toon(tex ? look.floor : "#d9d4cc", withMap(tex, { roughness: 0.85 }), "concrete"),
  );
  if (!hasPools) floor.rotation.x = -Math.PI / 2;
  floor.receiveShadow = true;
  root.add(floor);
  const contact = buildContactShadows(level);
  if (contact) root.add(contact);

  const surf: Surfaces = { plywood: plywoodTexture(), metal: paintedMetalTexture(), pieces: look.pieces ?? null };
  // Texture repeats are per meter of UV; one plywood sheet is 1.22 m.
  surf.plywood?.repeat.set(1 / 1.22, 1 / 1.22);
  surf.metal?.repeat.set(1 / 3, 1 / 3);

  const outside = look.indoor ? null : (look.outside ?? "park");
  if (outside === "park" || outside === "plaza" || outside === "beach" || outside === "street") {
    // Grass, paving or sand outside the fence. A rooftop has none: the street is far below.
    // It stops at the fence line on a side with the sea, so the swells aren't under it.
    const far = size * 2.5;
    const sea = (side: EdgeSide) => (level.edges?.some((e) => e.side === side && e.water) ? level.bounds : far);
    const [x0, x1, z0, z1] = [-sea("west"), sea("east"), -sea("north"), sea("south")];
    // On a hill (#193) it's cut into 20 m squares: sheared, a pair of huge
    // triangles loses enough depth precision to show through the road just above it.
    const seg = (len: number) => (level.hill ? Math.ceil(len / 20) : 1);
    const ground = new THREE.Mesh(
      hasPools ? groundPlane(level, x0, x1, z0, z1) : new THREE.PlaneGeometry(x1 - x0, z1 - z0, seg(x1 - x0), seg(z1 - z0)),
      toon(OUTSIDE_GROUND[outside], { roughness: 0.95 }, outside === "park" ? "grass" : "concrete"),
    );
    if (hasPools) ground.position.y = -0.02;
    else {
      ground.rotation.x = -Math.PI / 2;
      ground.position.set((x0 + x1) / 2, -0.02, (z0 + z1) / 2);
    }
    root.add(ground);
  }

  // Perimeter walls, one color per side; a soft side (#82) has a beach instead.
  const wh = look.wallHeight;
  for (let side = 0; side < 4; side++) {
    const edge = level.edges?.find((e) => e.side === WALL_SIDES[side]);
    if (edge) {
      root.add(buildBeach(level.bounds, side, edge.sand, edge.water));
      continue;
    }
    // On a street (#115) the building fronts are the north and south walls.
    if (outside === "street" && side % 2 === 0) continue;
    // A hill's road (#197) draws its own side walls, a chunk at a time, broken where side streets cross.
    if (level.hill?.road && side % 2 === 1) continue;
    // Painted block wall: the concrete texture, one block per 4 m x 2 m.
    const [len, out] = side % 2 === 0 ? [sizeX + 1, sizeZ / 2] : [sizeZ + 1, sizeX / 2];
    const angle = (side * Math.PI) / 2;
    // A street end (#236) leaves its stretch out: its barriers stand there instead; a shop row (#240) its shopfronts.
    const ends = (look.streetEnds ?? []).filter((e) => e.side === WALL_SIDES[side]);
    const rows = (look.shopRows ?? []).filter((r) => r.side === WALL_SIDES[side]);
    const cuts = [...ends, ...rows].map((e) => localSpan(e.side, e.from, e.to));
    const spans = cuts.length ? wallSpans(len, cuts) : [[-len / 2, len / 2] as [number, number]];
    for (const [s0, s1] of spans) {
      const piece = s1 - s0;
      const wallTex = tex?.clone() ?? null;
      wallTex?.repeat.set(piece / 4, wh / 2);
      const wall = new THREE.Mesh(
        new RoundedBoxGeometry(piece, wh, 0.5, 2, 0.08),
        toon(look.walls[side]!, withMap(wallTex, { roughness: 0.75 }), "paint"),
      );
      const mid = (s0 + s1) / 2;
      wall.position.set(Math.sin(angle) * (out + 0.25) + Math.cos(angle) * mid, wh / 2, Math.cos(angle) * (out + 0.25) - Math.sin(angle) * mid);
      wall.rotation.y = angle;
      wall.receiveShadow = true;
      if (look.indoor) wall.add(wallStripes(piece, wh));
      root.add(wall);
    }
    for (const e of ends) root.add(buildStreetEnd(e, out));
    for (const r of rows) root.add(buildShopRow(r, out));
  }

  for (const p of level.prims) root.add(buildPrim(p, surf));
  for (const r of level.rails) {
    // A post stands on the floor, or down in a pool (#163) where it's below it.
    const mesh = buildRail(r, surf, hasPools ? (x, z) => Math.min(0, surfaceAt(level, x, z).h) : () => 0);
    if (mesh) root.add(mesh);
  }

  if (level.scenery) root.add(buildScenery(level.scenery));
  if (hubs.length > 0) root.add(buildHubSigns(hubs, look));
  if (look.roof !== null) root.add(buildRoof(size, look.roof));
  if (outside === null) return root;
  if (outside === "beach") {
    // Beach town: buildings only behind the walls, the beaches' scenery on the soft sides.
    const walled = WALL_SIDES.flatMap((side, k) => (level.edges?.some((e) => e.side === side) ? [] : [k]));
    const wet = (x: number, z: number) => groundKind(level, x, z) === "water";
    // Nor where a street end's street and the buildings flanking it run out (#236).
    const ends = look.streetEnds ?? [];
    const street = (x: number, z: number) =>
      ends.some((e) => {
        const [along, past] = e.side === "north" || e.side === "south" ? [x, e.side === "north" ? -z : z] : [z, e.side === "west" ? -x : x];
        return past > fenceAt(level, e.side) && along > e.from - STREET_END_FLANK - 2 && along < e.to + STREET_END_FLANK + 2;
      });
    // No palms there either, nor in a shop row's buildings (#240), which stand where they would; the town's blocks go on behind a row.
    const bare = [...ends.map((e) => ({ ...e, flank: STREET_END_FLANK + 2 })), ...(look.shopRows ?? []).map((r) => ({ ...r, flank: 2 }))];
    const palmFree = (side: EdgeSide, t: number) =>
      bare.some((e) => {
        if (e.side !== side) return false;
        const [a, b] = localSpan(e.side, e.from, e.to);
        return t > a - e.flank && t < b + e.flank;
      });
    root.add(buildCity(level.bounds, 0, walled, (x, z) => wet(x, z) || street(x, z)), buildBeachScenery(level, palmFree));
    return root;
  }
  if (outside === "street") {
    root.add(buildStreet(sizeX / 2, sizeZ / 2, look.walls));
    return root;
  }
  if (outside !== "park") {
    root.add(buildCity(level.bounds, outside === "roof" ? -ROOF_DROP : 0));
    return root;
  }

  const b = level.bounds;
  const treeSpots: [number, number, number][] = [
    [-b - 6, -b + 5, 1.2],
    [-b - 8, 4, 1],
    [-b - 5, b - 8, 1.3],
    [b + 7, -b + 10, 1.1],
    [b + 5, 10, 0.9],
    [b + 9, b - 3, 1.3],
    [-10, -b - 8, 1.2],
    [14, -b - 6, 1],
    [-18, b + 7, 1.1],
    [20, b + 9, 1.3],
  ];
  for (const [x, z, s] of treeSpots) root.add(buildTree(x, z, s));

  return root;
}

const OUTSIDE_GROUND = { park: "#7cc96b", plaza: "#b8b0a4", beach: SAND, street: "#4d4d52" } as const;

/** How far below a rooftop park the street is (m). */
const ROOF_DROP = 30;
const BUILDING_COLORS = ["#c98b6b", "#d9c7a7", "#8fa3b8", "#b5795f", "#e0d2b8", "#9c8f86"];

/**
 * The buildings round a `plaza` or `roof` park: a ring of blocks standing on
 * a street at `base` (0 beside a plaza, far below a rooftop), with a band of
 * windows. Fixed layout, so every build of a map looks the same; the fog
 * hides the far ones. `sides` picks which sides get a row (all four by
 * default), and a block with either end where `skip` says is left out (a beach
 * park's sea).
 */
function buildCity(
  bounds: number,
  base: number,
  sides: readonly number[] = [0, 1, 2, 3],
  skip: (x: number, z: number) => boolean = () => false,
): THREE.Object3D {
  const group = new THREE.Group();
  if (base < 0) {
    const street = new THREE.Mesh(new THREE.PlaneGeometry(bounds * 12, bounds * 12), toon("#6f6a66", { roughness: 0.95 }, "concrete"));
    street.rotation.x = -Math.PI / 2;
    street.position.y = base;
    group.add(street);
    // The building the park is on, just inside the parapet.
    const under = withEdges(new THREE.Mesh(new THREE.BoxGeometry(bounds * 2 + 1, -base, bounds * 2 + 1), toon("#a08a7a", { roughness: 0.85 }, "concrete")));
    under.position.y = base / 2 - 0.03;
    group.add(under);
  }
  const glass = toon("#bfe3ff", { emissive: "#6f9fc4", emissiveIntensity: 0.35 }, "glass");
  // Round each side of the fence, a gap (a street) then a row of blocks.
  const gap = base < 0 ? 10 : 14;
  let k = 0;
  for (const side of sides) {
    const angle = (side * Math.PI) / 2;
    const along = new THREE.Vector3(Math.cos(angle), 0, -Math.sin(angle));
    const out = new THREE.Vector3(Math.sin(angle), 0, Math.cos(angle));
    for (let t = -bounds - gap; t < bounds + gap; k++) {
      // A fixed, uneven rhythm of widths, depths and heights.
      const w = 8 + ((k * 5) % 7);
      const d = 8 + ((k * 3) % 5);
      const top = base < 0 ? -2 + ((k * 7) % 11) : 8 + ((k * 7) % 13);
      const h = top - base;
      const c = t + w / 2;
      const at = along.clone().multiplyScalar(c).addScaledVector(out, bounds + gap + d / 2);
      const [a0, a1] = [c - w / 2, c + w / 2].map((e) => along.clone().multiplyScalar(e).addScaledVector(out, bounds + gap));
      if (skip(a0!.x, a0!.z) || skip(a1!.x, a1!.z)) {
        t += w + 1.5;
        continue;
      }
      const block = withEdges(new THREE.Mesh(new THREE.BoxGeometry(w, h, d), toon(BUILDING_COLORS[k % BUILDING_COLORS.length]!, { roughness: 0.85 }, "concrete")));
      block.position.set(at.x, base + h / 2, at.z);
      block.rotation.y = angle;
      // A band of windows on the face toward the park, near the top.
      const band = new THREE.Mesh(new THREE.BoxGeometry(w * 0.8, 1.2, 0.1), glass);
      band.position.set(0, h / 2 - 2.2, -d / 2 - 0.05);
      block.add(band);
      group.add(block);
      t += w + 1.5;
    }
  }
  return group;
}

/** Warehouse wall details on both faces: a dark skirting band and a row of high windows. */
function wallStripes(len: number, h: number): THREE.Object3D {
  const group = new THREE.Group();
  const skirt = new THREE.Mesh(new THREE.BoxGeometry(len, 1.2, 0.56), toon("#3d3440", {}, "paint"));
  skirt.position.y = -h / 2 + 0.6;
  group.add(skirt);
  const glass = toon("#bfe3ff", { emissive: "#6f9fc4", emissiveIntensity: 0.6 }, "glass");
  const windows = Math.floor(len / 6);
  for (let i = 0; i < windows; i++) {
    const pane = withEdges(new THREE.Mesh(new THREE.BoxGeometry(3.2, 1.6, 0.58), glass));
    pane.position.set(-len / 2 + (i + 0.5) * (len / windows), h / 2 - 2, 0);
    group.add(pane);
  }
  return group;
}

/**
 * A flat roof with girders and glowing skylight strips. It doesn't cast
 * shadows, so the "sun" (daylight through the skylights) still lights the floor.
 */
function buildRoof(size: number, y: number): THREE.Object3D {
  const group = new THREE.Group();
  const roof = new THREE.Mesh(new THREE.PlaneGeometry(size + 1, size + 1), toon("#4a4150", { side: THREE.DoubleSide }, "metal"));
  roof.rotation.x = Math.PI / 2;
  roof.position.y = y;
  group.add(roof);
  const girder = toon("#2f2934", {}, "paintedMetal");
  const light = new THREE.MeshBasicMaterial({ color: "#fff6dc" });
  const count = Math.floor(size / 8);
  for (let i = 0; i < count; i++) {
    const x = -size / 2 + (i + 0.5) * (size / count);
    const beam = withEdges(new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.8, size), girder));
    beam.position.set(x, y - 0.4, 0);
    group.add(beam);
    if (i % 2 === 0) {
      const strip = new THREE.Mesh(new THREE.PlaneGeometry(2.4, size * 0.8), light);
      strip.rotation.x = Math.PI / 2;
      strip.position.set(x + size / count / 2, y - 0.02, 0);
      group.add(strip);
    }
  }
  return group;
}

/**
 * Baked contact shadows: a transparent dark layer just above the floor,
 * darkest where ramps, boxes and walls meet the ground.
 */
function buildContactShadows(level: Level): THREE.Mesh | null {
  // A box barely off the floor (#115: a street's asphalt, a painted line) is
  // paint on the ground: no shadow, which would also show through it far off.
  const standing = level.prims.filter((p) => !(p.kind === "box" && p.h < FLAT_BOX_H));
  const footprints = standing.map((p) => {
    const kind = primKind(p);
    const [u0, u1] = kind.extent(p);
    return { x: p.x, z: p.z, rot: p.rot, u0, u1, hw: p.hw, strength: kind.shadow };
  });
  const tex = contactShadowTexture(level.bounds, footprints);
  if (!tex) return null;
  const size = level.bounds * 2;
  const mat = new THREE.MeshBasicMaterial({
    color: 0x1a1426,
    alphaMap: tex,
    transparent: true,
    opacity: 0.75,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(size, size), mat);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = 0.004;
  mesh.renderOrder = 1;
  return mesh;
}

/**
 * Frees a map's GPU resources: its geometries, and its materials with their
 * textures. The shared ink materials (mesh outlines and park edges) stay.
 */
export function disposeWorld(root: THREE.Object3D): void {
  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh || obj instanceof THREE.LineSegments)) return;
    obj.geometry.dispose();
    if (obj instanceof THREE.LineSegments || obj.name === "outline") return;
    for (const m of Array.isArray(obj.material) ? obj.material : [obj.material]) {
      if (m instanceof THREE.MeshBasicMaterial) m.alphaMap?.dispose(); // the baked contact shadows
      if (m instanceof THREE.MeshStandardMaterial) for (const t of [m.map, m.normalMap, m.roughnessMap]) t?.dispose();
      (m as THREE.Material).dispose();
    }
  });
}
